import { BigNumber } from 'bignumber.js'
import { getAddress, isAddressEqual, zeroAddress } from 'viem'
import type { MarketTemplate, UserPositionStatus } from '@/llamalend/llamalend.types'
import type { AssetDetails, LlamaMarket } from '@/llamalend/queries/market-list/llama-markets'
import type { INetworkName as LlamaNetworkId } from '@curvefi/llamalend-api/lib/interfaces'
import { LendMarketTemplate } from '@curvefi/llamalend-api/lib/lendMarkets'
import { MintMarketTemplate } from '@curvefi/llamalend-api/lib/mintMarkets'
import { Chain } from '@curvefi/prices-api'
import { getUserMarketCollateralEvents as getMintUserMarketCollateralEvents } from '@curvefi/prices-api/crvusd'
import { getUserMarketCollateralEvents as getLendUserMarketCollateralEvents } from '@curvefi/prices-api/lending'
import type { BadDebt } from '@curvefi/prices-api/liquidations'
import type { ActivityToken } from '@evm-ui/features/activity-table/types'
import { getLib, requireLib } from '@evm-ui/features/connect-wallet'
import type { Wallet } from '@evm-ui/features/connect-wallet/lib/types'
import { MarketType, MarketVersion } from '@evm-ui/types/market'
import { CRVUSD } from '@evm-ui/utils'
import { type Address, Hex, type Token } from '@primitives/address.utils'
import type { Amount, Decimal } from '@primitives/decimal.utils'
import { MarketAssetsType } from '@primitives/llamalend/markets.constants'
import {
  type Nullish,
  type AllOrNone,
  assert,
  DEFAULT_DECIMALS,
  maybe,
  maybes,
  notFalsy,
} from '@primitives/objects.utils'
import { RouteProviders } from '@primitives/router.utils'
import { type MetricProps } from '@ui/components/Metric'
import type { TokenOrPair } from '@ui/components/TokenIcons'
import { SLIPPAGE } from '@ui/features/forms/slippage/slippage.utils'
import { combineQueries } from '@ui/features/queries/combine'
import { QueryProp, toQuery } from '@ui/features/queries/util'
import { decimal, decimalMinus, decimalMultiply, decimalSum } from '@ui/lib/decimal'
import { ReleaseChannel } from '@ui/lib/env'
import { t } from '@ui/lib/i18n'
import { formatCappedRatePercent } from '@ui/lib/rates.utils'
import { formatToken } from '@ui/lib/tokens'
import { getMarketAssetsType } from './market-assets-type.utils'
// eslint-disable-next-line no-restricted-imports -- the only reader of ZAP_V0_MINT_MARKETS, through isZapV0MintMarket
import { LP_TOKEN_PAIRS, MARKETS_LEVERAGE_CONFIG, SOLVENCY_THRESHOLDS, ZAP_V0_MINT_MARKETS } from './markets.constants'
import type { UserState } from './queries/user/user-state.query'
import type { MarketBorrowAprTooltipContentProps } from './widgets/tooltips/MarketBorrowAprTooltipContent'
import type { MarketSupplyRateTooltipContentProps } from './widgets/tooltips/MarketSupplyRateTooltipContent'

/**
 * Gets a Llama market (either a mint or lend market) by its ID.
 * Throws an error if no market is found with the given ID.
 */
export const getMarket = (id: string | MarketTemplate, lib = requireLib('llamaApi')): MarketTemplate =>
  typeof id === 'string' ? (id.startsWith('one-way') ? lib.getLendMarket(id) : lib.getMintMarket(id)) : id

/**
 * Helper to retrieve the llama market after initialization, avoiding crashing the components using it.
 * We use this helper during query validation since we cannot crash the validation suite outside `test()`
 */
export const tryGetMarket = (marketId: MarketTemplate | string | Nullish) =>
  typeof marketId === 'object' ? marketId : maybes([marketId, getLib('llamaApi')], getMarket)

const SLIPPAGE_KEY_BY_ASSETS_TYPE = {
  [MarketAssetsType.Correlated]: 'stable',
  [MarketAssetsType.BlueChip]: 'leverage',
  [MarketAssetsType.LongTail]: 'leverage',
} satisfies Record<MarketAssetsType, keyof typeof SLIPPAGE>

/** Returns the leverage slippage for the market's assets type, falling back to the default for unmapped markets. */
export const getMarketLeverageSlippage = (chainId: number, controllerAddress: Address | undefined) =>
  SLIPPAGE[
    maybe(getMarketAssetsType(chainId, controllerAddress), type => SLIPPAGE_KEY_BY_ASSETS_TYPE[type]) ?? 'leverage'
  ].default

/**
 * Resolves leverage providers from the market whitelist: approved markets get every provider on Beta and only their
 * explicitly tested providers on Stable/Legacy, while unlisted markets remain disabled.
 */
export const getMarketLeverageProviders = (
  chainId: number,
  controllerAddress: Address | undefined,
  releaseChannel: ReleaseChannel,
) => {
  const config = controllerAddress && MARKETS_LEVERAGE_CONFIG[chainId]?.[getAddress(controllerAddress)]
  return maybe(config, config => (releaseChannel === ReleaseChannel.Beta ? RouteProviders : config.providers))
}

/**
 * Checks if a market supports leverage or not. A market supports leverage if:
 * - ZapV2 is available
 * - A static legacy Mint market has its own V0 leverage zap
 */
export const hasLeverage = <T extends MarketTemplate | undefined>(market: T) =>
  maybe(market, market => hasZapV2(market) || hasLegacyMintLeverage(market))

/**
 * Checks if leverage value (multiplier) can be calculated and displayed for this market.
 *
 * Returns true for:
 * - Markets with ZapV2 leverage support
 *
 * Note: Some older Mint markets (marketId < 6) support leverage operations (open/close positions)
 * but cannot calculate the leverage multiplier value.
 */
export const hasLeverageValue = <T extends MarketTemplate | Nullish>(market: T) => hasZapV2(market)

export const isLendMarket = (market: MarketTemplate | Nullish): market is LendMarketTemplate =>
  market instanceof LendMarketTemplate

export const isMintMarket = (market: MarketTemplate | Nullish): market is MintMarketTemplate =>
  market instanceof MintMarketTemplate

export const hasLegacyMintLeverage = (market: MarketTemplate) =>
  isMintMarket(market) && market.index == null && market.leverageZap !== zeroAddress

export const isZapV0MintMarket = (blockchainId: Chain | undefined, controllerAddress: Address | undefined) =>
  maybes([blockchainId, controllerAddress], (blockchainId, controllerAddress) =>
    ZAP_V0_MINT_MARKETS[blockchainId]?.includes(controllerAddress.toLowerCase() as Address),
  )

const hasV1Deleverage = (market: MarketTemplate) =>
  isMintMarket(market) && market.index == null && market.deleverageZap !== zeroAddress

export const hasDeleverage = (market: MarketTemplate) => hasZapV2(market) || hasV1Deleverage(market)

const isV2Market = (market: MarketTemplate | Nullish) =>
  maybe(market, market => isLendMarket(market) && market.version === 'v2')

export const hasResetPosition = (market: MarketTemplate | Nullish): market is LendMarketTemplate<'v2'> =>
  isV2Market(market) === true

/**
 * Check if an open position is a leveraged position, using the leverage value.
 * prevLeverage is 0 when the position didn't exist before, future leverage is 0 on full repayment.
 * (prev)Leverage is >0 and <1 when the position has been leveraged in the past or went through soft liquidation.
 * (prev)Leverage is 1 when the position is not leveraged at all (simple borrowing, no leverage).
 * (prev)Leverage is > 1 when the position is leveraged.
 * @deprecated Checks a collateral leverage ratio. Use `isZapLeveraged` from `useUserCollateralEvents` to know if the
 * position borrowed through the zap, and `useEquityLeverage` for the value.
 **/
export const isPositionLeveraged = (leverage: Amount | Nullish) =>
  leverage != null && !BigNumber(leverage).isZero() && !BigNumber(leverage).isEqualTo(1)

export const canRepayFromStateCollateral = <T extends MarketTemplate | undefined>(market: T) =>
  maybe(market, market => (isMintMarket(market) ? hasDeleverage(market) : hasLeverage(market)))

export const canLeverageUserBorrowed = <T extends MarketTemplate | undefined>(market: T) =>
  maybe(market, market => hasLegacyMintLeverage(market))

export const hasVault = (market: MarketTemplate) => isLendMarket(market) && 'vault' in market

export const hasZapV2 = <T extends MarketTemplate | Nullish>(market: T) =>
  maybe(market, market => market.leverageZapV2.hasLeverage())

/** LLv1 markets whose controller supports delegation use the delegation ZapV2 (no router calldata size limit). */
const hasV1ZapDelegation = (market: LendMarketTemplate) => {
  const delegationZap = market.getLlamalend().constants.ALIASES.leverage_zap_v2_transient
  return (
    market.version === 'v1' &&
    !!delegationZap &&
    isAddressEqual(market.getZapAddress() as Address, delegationZap as Address)
  )
}

/** LLv2 markets and the newer LLv1 markets use the delegation ZapV2 contract */
export const hasZapDelegation = (market: MarketTemplate | Nullish) =>
  maybe(market, market => isLendMarket(market) && (market.version === 'v2' || hasV1ZapDelegation(market)))

export const isRouterRequired = (
  type: 'zapV2' | 'V0' | 'deleverage' | 'unleveragedMint' | 'unleveragedLend' | 'unleveraged',
) => type == 'zapV2'

const LEVERAGED_MAX_BORROW_RATIO = '0.99999' // -0.001%

/** Leaves a buffer below the quoted maximum to account for leverage quote changes before execution. */
export const getMaxBorrowAmount = (maxDebt: Decimal | undefined, leverageEnabled: boolean | undefined) =>
  maxDebt && leverageEnabled ? decimalMultiply(maxDebt, LEVERAGED_MAX_BORROW_RATIO) : maxDebt

export const hasGauge = (market: MarketTemplate) => isLendMarket(market) && market.addresses.gauge !== zeroAddress

export const getLendMarketVersion = (market: LendMarketTemplate): MarketVersion =>
  assert(
    { v1: MarketVersion.v1, v2: MarketVersion.v2 }[market.version],
    `Unsupported LlamaLend market version: ${market.version}`,
  )

const getBorrowSymbol = (market: MarketTemplate) =>
  isMintMarket(market) ? CRVUSD.symbol : market.borrowed_token.symbol

const getCollateralSymbol = (market: MarketTemplate) =>
  isMintMarket(market) ? market.collateralSymbol : market.collateral_token.symbol

export const formatTokenAmounts = (
  market: MarketTemplate,
  { userBorrowed, userCollateral }: { userBorrowed?: Decimal; userCollateral?: Decimal },
) =>
  notFalsy(
    userBorrowed && +userBorrowed && formatToken(userBorrowed, getBorrowSymbol(market), 'amount'),
    userCollateral && +userCollateral && formatToken(userCollateral, getCollateralSymbol(market), 'amount'),
  ).join(', ')

export type MarketToken = Pick<AssetDetails, 'symbol' | 'address' | 'decimals'>

export type MarketTokens = { collateralToken: MarketToken; borrowToken: MarketToken }

/** Accepts either both tokens or an empty object. Avoid Partial<> because it could allow one of the tokens only */
export type MarketTokensOrEmpty = AllOrNone<MarketTokens>

type MarketOrApiValue<T, Value> = T extends MarketTemplate ? Value : Value | undefined

const getMarketOrApiValue = <T extends MarketTemplate | Nullish, Value>(
  market: T,
  apiMarket: LlamaMarket | undefined,
  getMarketValue: (market: MarketTemplate) => Value,
  getApiValue: (apiMarket: LlamaMarket) => Value,
): MarketOrApiValue<T, Value> =>
  maybe(market, getMarketValue) ?? (maybe(apiMarket, getApiValue) as MarketOrApiValue<T, Value>)

export const getMarketType = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, MarketType> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    m => (isLendMarket(m) ? MarketType.Lend : MarketType.Mint),
    m => m.type,
  )

export const getMarketEndpoint = (marketType: MarketType) =>
  (({ [MarketType.Mint]: 'crvusd', [MarketType.Lend]: 'lending' }) as const)[marketType]

export const getTokens = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, MarketTokens> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    (market: MarketTemplate): MarketTokens =>
      isMintMarket(market)
        ? {
            collateralToken: {
              symbol: market.collateralSymbol,
              address: market.collateral as Address,
              decimals: market.collateralDecimals,
            },
            borrowToken: CRVUSD,
          }
        : {
            collateralToken: {
              symbol: market.collateral_token.symbol,
              address: market.collateral_token.address as Address,
              decimals: market.collateral_token.decimals,
            },
            borrowToken: {
              symbol: market.borrowed_token.symbol,
              address: market.borrowed_token.address as Address,
              decimals: market.borrowed_token.decimals,
            },
          },
    ({ assets }) => ({ collateralToken: assets.collateral, borrowToken: assets.borrowed }),
  )

/** Returns the token pair of a known LP token */
const findLpTokenPair = (blockchainId: Chain, address: string) => LP_TOKEN_PAIRS[blockchainId]?.[getAddress(address)]

/** Returns the token pair of a known LP token, or the token itself */
export const getTokenOrPair = (blockchainId: Chain, token: Token): TokenOrPair =>
  findLpTokenPair(blockchainId, token.address) ?? token

/** Returns the addresses of the token pair of a known LP token, or the address itself */
export const getAddressOrPair = <T extends string | Nullish>(
  blockchainId: Chain | Nullish,
  address: T,
): T | Address[] =>
  maybe(maybes([blockchainId, address], findLpTokenPair), ([first, second]): Address[] => [
    first.address,
    second.address,
  ]) ?? address

/** Returns the token of an activity row, with the addresses of its token pair when it is a known LP token */
export const getActivityToken = (blockchainId: Chain, token: Token | Nullish): ActivityToken | undefined =>
  maybe(token, ({ symbol, address }) => ({ symbol, address: getAddressOrPair(blockchainId, address) }))

export const getAmmAddress = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, Address> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    market => (isLendMarket(market) ? market.addresses.amm : market.address) as Address,
    m => m.ammAddress,
  )

export const getControllerAddress = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, Address> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    market => (isLendMarket(market) ? market.addresses.controller : market.controller) as Address,
    m => m.controllerAddress,
  )

export const getVaultAddress = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, Address | null> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    market => (isLendMarket(market) ? (market.addresses.vault as Address) : null),
    m => m.vaultAddress,
  )

export const getGaugeAddress = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, Address | null> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    market =>
      isLendMarket(market) && market.addresses.gauge !== zeroAddress ? (market.addresses.gauge as Address) : null,
    m => m.supplyGaugeAddress,
  )

export const getVaultToken = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, MarketToken | undefined> =>
  maybe(getVaultAddress(market, apiMarket), address => ({
    address,
    symbol: t`Vault shares`,
    decimals: DEFAULT_DECIMALS,
  }))

export type BandRange = { minBands: number; maxBands: number }
export type BandRangeOrEmpty = AllOrNone<BandRange>

export const getMarketBandRange = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, BandRange | undefined> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    m => ({ minBands: +m.minBands, maxBands: +m.maxBands }),
    m => maybes([m.minBand, m.maxBand], (minBands, maxBands) => ({ minBands, maxBands })),
  )

export const getCrvTokenAddress = (market: MarketTemplate | Nullish): Address | undefined =>
  maybe(market, m => m.getLlamalend().constants.ALIASES.crv as Address)

export const getMonetaryPolicy = <T extends MarketTemplate | Nullish>(
  market: T,
  apiMarket?: LlamaMarket,
): MarketOrApiValue<T, Address | undefined> =>
  getMarketOrApiValue(
    market,
    apiMarket,
    market => (isLendMarket(market) ? market.addresses.monetary_policy : market.monetaryPolicy) as Address,
    m => m.monetaryPolicyAddress,
  )

/**
 * Calculates the loan-to-value ratio of a market.
 * @param debtAmount - The amount of debt in the market.
 * @param collateralAmount - The amount of deposited collateral.
 * @param collateralBorrowTokenAmount - The amount of collateral that has been converted into the borrow token during soft-liquidation.
 * @param borrowTokenUsdRate - The USD rate of the borrow token.
 * @param collateralTokenUsdRate - The USD rate of the collateral token.
 * @returns The loan-to-value ratio of the market.
 */
export const calculateLtv = (
  debtAmount: number,
  collateralAmount: number,
  collateralBorrowTokenAmount: number,
  borrowTokenUsdRate: number | Nullish,
  collateralTokenUsdRate: number | Nullish,
) => {
  const collateralValue =
    collateralAmount * (collateralTokenUsdRate ?? 0) + collateralBorrowTokenAmount * (borrowTokenUsdRate ?? 0)
  const debtValue = debtAmount * (borrowTokenUsdRate ?? 0)
  if (collateralValue === 0 || debtValue === 0) return 0
  return (debtValue / collateralValue) * 100
}

export const calculateLendMarketTvlUsd = ({
  borrowedBalanceUsd,
  collateralBalanceUsd,
  totalAssetsUsd,
  totalDebtUsd,
}: {
  borrowedBalanceUsd: number
  collateralBalanceUsd: number
  totalAssetsUsd: number
  totalDebtUsd: number
}) => borrowedBalanceUsd + collateralBalanceUsd + totalAssetsUsd - totalDebtUsd

export const calculateMintMarketTvlUsd = ({ collateralAmountUsd }: { collateralAmountUsd: number }) =>
  collateralAmountUsd

/**
 * Sends a new transaction hash to the backend to update user events.
 * Note that the backend data will not be directly updated, but this will trigger a background refresh.
 * Therefore, we don't invalidate the `userLendCollateralEvents` query immediately after calling this function.
 */
export const updateUserEventsApi = async (
  wallet: Wallet,
  { blockchainId }: { blockchainId: LlamaNetworkId },
  market: MarketTemplate,
  txHash: string,
) => {
  const [address, updateEvents] = isLendMarket(market)
    ? [market.addresses.controller, getLendUserMarketCollateralEvents]
    : [market.controller, getMintUserMarketCollateralEvents]
  return await updateEvents(wallet.address, blockchainId, address as Address, txHash as Hex)
}

/**
 * Returns the minimum crvUSD stablecoin balance in a user’s AMM bands that must be
 * exceeded before the position is considered to be in soft liquidation.
 *
 * When a user briefly enters and then exits soft liquidation, one or more bands may
 * be left with a tiny amount of crvUSD (dust). There is a dust-sweeping bot that
 * cleans this up, but there can be a delay before those remnants are collected.
 * Without a threshold this dust would cause false-positive soft-liquidation alerts.
 *
 * However, the threshold must be skipped when the oracle price is within or near the
 * user’s bands (`userIsCloseToLiquidation=true`), because in that case even a tiny
 * stablecoin balance is genuine soft-liquidation activity, not leftover dust. Dust
 * only exists when the oracle has moved *far away* from the bands, which is exactly
 * the situation where `userIsCloseToLiquidation` is false.
 *
 * If somebody wants to tackle this properly, they can find the bot code here:
 * https://github.com/curvefi/dust-cleaner-bot/blob/0795b2fa/app/services/controller.py#L90
 */
const getSoftLiquidationThreshold = (userIsCloseToLiquidation: boolean) => (userIsCloseToLiquidation ? 0 : 0.1)

/**
 * Whether the oracle price has dropped below the user's band range.
 * Band numbers go up as prices go down, so the user's lower price boundary is the higher band
 * number (n2, or `userBandsValue[1]` after `reverseBands`). If the active/oracle-price band has
 * moved past it, the price is below the user's range and their collateral has fully converted.
 */
export const isBelowRange = (activeBand: number | Nullish, lowerBoundBand: number | Nullish) =>
  activeBand != null && lowerBoundBand != null && activeBand > lowerBoundBand

/**
 * Picks the health value to display to the user. Health is the buffer before full liquidation,
 * which happens at health = 0 (see the liquidation-protection docs).
 *
 * `healthNotFull` values collateral at each band's mid-price only — it's the buffer as measured
 * inside the liquidation-protection range, ignoring any price cushion above the range.
 * `healthFull` adds that above-range cushion on top, so above the range `healthFull >= healthNotFull`
 * and inside/below the range they're equal.
 *
 * We display `healthFull` by default because it reflects the user's actual current buffer. But when
 * `healthNotFull` is negative the band-valued collateral no longer covers the debt, meaning the user
 * is one drop into the range away from full liquidation — so we surface that pessimistic value as a
 * warning even if `healthFull` would still look positive.
 *
 * See https://docs.curve.finance/user/llamalend/liquidation-protection/how-it-works.
 */
export const getDisplayHealth = (
  healthFull: Decimal | number | Nullish,
  healthNotFull: Decimal | number | Nullish,
): number | null => {
  if (healthFull == null || healthNotFull == null) return null
  return +(+healthNotFull < 0 ? healthNotFull : healthFull)
}

/**
 * healthNotFull is needed here because:
 * User full health can be > 0
 * But user is at risk of liquidation if not full < 0
 */
export function getLiquidationStatus(
  healthNotFull: Decimal | undefined,
  userIsCloseToSoftLiquidation: boolean,
  userIsBelowRange: boolean,
  userStateCollateral: Decimal | undefined,
  userStateBorrowed: Decimal | undefined,
): UserPositionStatus {
  if (healthNotFull == null || userStateCollateral == null || userStateBorrowed == null) return undefined
  const threshold = getSoftLiquidationThreshold(userIsCloseToSoftLiquidation)
  if (+healthNotFull < 0) return 'hardLiquidation' as const
  if (userIsBelowRange && +userStateCollateral > 0) return 'incompleteConversion' as const
  if (userIsBelowRange && +userStateCollateral <= 0) return 'fullyConverted' as const
  if (+userStateBorrowed > threshold) return 'softLiquidation' as const
  return 'healthy' as const
}

export const getIsUserCloseToSoftLiquidation = (userFirstBand: number, oraclePriceBand: number | Nullish) =>
  oraclePriceBand != null && userFirstBand <= oraclePriceBand + 2

/**
 * Formats a collateral + borrowed notional string, e.g. "1.5K WETH + 200 crvUSD".
 * Returns undefined if collateral value is not available.
 */
export const formatCollateralNotional = (
  collateral: { value: Decimal | Nullish; symbol: string | undefined },
  borrow: { value: Decimal | Nullish; symbol: string | undefined } | undefined,
): string | undefined =>
  notFalsy(
    collateral.value && +collateral.value && collateral.symbol && formatToken(collateral.value, collateral.symbol),
    borrow?.value && +borrow?.value && borrow.symbol && formatToken(borrow.value, borrow.symbol),
  ).join(' + ')

export const formatEstimatedNetBorrowRate = (rate: number | Nullish) => t`Est. net ${formatCappedRatePercent(rate)}`

export const formatNetSupplyRate = (rate: number | Nullish) => t`Total ${formatCappedRatePercent(rate)}`

/** Whether the market has extras (collateral yield or borrow rewards) that make up an estimated net borrow APR. */
export const hasEstimatedNetBorrowApr = ({
  extraRewards,
  rebasingYieldApr,
}: Pick<MarketBorrowAprTooltipContentProps, 'extraRewards' | 'rebasingYieldApr'>) =>
  !!extraRewards?.length || rebasingYieldApr != null

/** Whether the market has extras (incentives with CRV, rewards or borrowed token yield) that make up a net supply APY. */
export const hasNetSupplyRate = ({
  extraRewards,
  extraIncentives,
  rebasingYieldApy,
}: Pick<MarketSupplyRateTooltipContentProps, 'extraRewards' | 'extraIncentives' | 'rebasingYieldApy'>) =>
  !!extraRewards.length || !!extraIncentives.length || rebasingYieldApy != null

/** Compute utilization percentage from available liquidity and total assets. */
export const getUtilizationPercent = (available: Decimal | undefined, totalAssets: Decimal | undefined) => {
  if (available == null || totalAssets == null) return undefined
  const total = +totalAssets
  if (total === 0) return undefined
  return ((total - +available) / total) * 100
}

/**
 * Calculates the return to wallet amounts for a given receive amount.
 */
export function calculateReturnToWallet({
  totalBorrowed = '0',
  userState,
  stateCollateralDelta = '0',
  collateralSymbol,
  borrowedSymbol,
}: {
  totalBorrowed: Decimal | undefined
  userState: UserState | undefined
  stateCollateralDelta: Decimal | undefined
  collateralSymbol: string
  borrowedSymbol: string
}): { value: Decimal; symbol: string }[] {
  const { debt: stateDebt = '0', collateral: stateCollateral = '0', stablecoin: stateBorrowed = '0' } = userState ?? {}
  if (+totalBorrowed > 0) {
    const returnCollateral = decimalMinus(stateCollateral, stateCollateralDelta)
    const returnBorrowed = decimalMinus(decimalSum(totalBorrowed, stateBorrowed), stateDebt)
    return notFalsy(
      +returnCollateral > 0 && { value: returnCollateral, symbol: collateralSymbol },
      +returnBorrowed > 0 && { value: returnBorrowed, symbol: borrowedSymbol },
    )
  }
  const returnBorrowed = decimalMinus(stateBorrowed, stateDebt)
  return notFalsy(
    { value: stateCollateral, symbol: collateralSymbol },
    +returnBorrowed > 0 && { value: returnBorrowed, symbol: borrowedSymbol },
  )
}

export const createGetBadDebtMarket = (badDebtMarkets: BadDebt | undefined) => {
  const toKey = (blockchainId: Chain, controllerAddress: Address) =>
    `${blockchainId}:${controllerAddress.toLowerCase()}`
  const badDebtByMarket = new Map(
    (badDebtMarkets ?? []).map(market => [toKey(market.chain, market.controllerAddress), market]),
  )
  return (blockchainId: Chain, controllerAddress: Address) =>
    badDebtByMarket.get(toKey(blockchainId, controllerAddress))?.badDebt
}

export const calculateMarketSolvency = ({
  totalAssetsUsd,
  badDebtUsd,
}: {
  totalAssetsUsd: number
  badDebtUsd: number | undefined
}) => (totalAssetsUsd && badDebtUsd != null ? (Math.max(0, totalAssetsUsd - badDebtUsd) / totalAssetsUsd) * 100 : null)

export const lowSolvencyDeprecatedMessage = (solvencyPercent: number | null) =>
  solvencyPercent != null && solvencyPercent < SOLVENCY_THRESHOLDS.low
    ? t`This market is deprecated due to low solvency`
    : null

export const getZapAddress = <T extends MarketTemplate | undefined>(market: T) =>
  maybe(market, m => (hasZapV2(m) ? (m.getZapAddress() as Address) : undefined))

/** Builds Metric props for a token-denominated value with the matching USD notional shown below it. */
export const tokenMetric = ({
  value,
  symbol,
  usdRate,
  notional,
}: {
  value: MetricProps['value']
  symbol: string | Nullish
  usdRate?: QueryProp<Amount>
  notional?: MetricProps['notional']
}) =>
  ({
    value,
    valueOptions: { abbreviate: true, unit: maybe(symbol, symbol => ({ symbol, position: 'suffix' as const })) },
    notional:
      notional ??
      (usdRate &&
        combineQueries([toQuery(value), usdRate], (value, usdRate) =>
          maybe(decimal(value), value => ({ value: decimalMultiply(value, usdRate), unit: 'dollar' as const })),
        )),
  }) satisfies Pick<MetricProps, 'value' | 'valueOptions' | 'notional'>
